import { Controller } from "@hotwired/stimulus"
import { runVortexTransition } from "../utils/masquerade_vortex"

// La porte du mode arcade, dans les deux sens.
//
// Deux usages, un seul contrôleur parce que c'est la même mécanique : jouer le vortex, puis
// poster. Seul le nombre de clics change.
//
//   - cinq clics sur la pastille d'un gardien (POST /arcade) ;
//   - un clic sur « Quitter le mode arcade » dans le menu du compte (DELETE /arcade).
//
// Pourquoi un formulaire construit à la main plutôt qu'un `button_to` : la bascule doit
// partir APRÈS l'implosion, pas au clic. On garde donc la main sur l'instant du départ, et
// la navigation reste une navigation pleine page — le document sortant reste peint pendant
// la requête, donc l'animation couvre toute la latence (cf. masquerade_vortex.ts).
//
// Le compteur retombe tout seul : cinq clics, c'est cinq clics d'affilée. Sans ça, cinq
// visites sur la même fiche au fil de la journée finiraient par ouvrir la porte toutes
// seules, et l'easter egg n'en serait plus un.
export default class extends Controller {
  static values = {
    payload: String,
    url: String,
    method: { type: String, default: "post" },
    clicks: { type: Number, default: 1 },
    resetAfter: { type: Number, default: 2500 },
    userId: String,
  }

  connect() {
    this.count = 0
    this.resetTimer = null
    this.departed = false
  }

  disconnect() {
    window.clearTimeout(this.resetTimer)
  }

  trigger(event) {
    // La pastille du gardien est aussi un bouton d'upload de photo quand c'est la sienne ;
    // ici on ne veut ni modale, ni navigation, ni soumission de formulaire parent.
    event.preventDefault()
    event.stopPropagation()

    if (this.departed) return

    this.count += 1
    this.#showCharge()

    window.clearTimeout(this.resetTimer)
    this.resetTimer = window.setTimeout(() => this.#reset(), this.resetAfterValue)

    if (this.count < this.clicksValue) return

    this.departed = true
    window.clearTimeout(this.resetTimer)
    runVortexTransition(this.payloadValue, () => this.#submit())
  }

  // Un peu de retour visuel à partir de l'avant-dernier clic : sans ça, on tape cinq fois
  // dans le vide et rien ne dit qu'il se passe quelque chose.
  #showCharge() {
    const ratio = Math.min(this.count / this.clicksValue, 1)
    this.element.style.setProperty("--arcade-charge", String(ratio))
    this.element.classList.add("arcade-portal--charging")
    this.element.classList.toggle("arcade-portal--almost", this.count >= this.clicksValue - 1)
  }

  #reset() {
    this.count = 0
    this.element.style.removeProperty("--arcade-charge")
    this.element.classList.remove("arcade-portal--charging", "arcade-portal--almost")
  }

  #submit() {
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.urlValue
    form.style.display = "none"
    // Turbo remplacerait le corps sans quitter le document : l'overlay du vortex partirait
    // avec, et la scène d'arrivée n'aurait plus rien à reprendre.
    form.setAttribute("data-turbo", "false")

    this.#appendField(form, "authenticity_token", this.#csrfToken())
    if (this.methodValue.toLowerCase() !== "post") {
      this.#appendField(form, "_method", this.methodValue)
    }
    if (this.hasUserIdValue && this.userIdValue) {
      this.#appendField(form, "user_id", this.userIdValue)
    }

    document.body.appendChild(form)
    form.submit()
  }

  #appendField(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value ?? ""
    form.appendChild(input)
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? ""
  }
}
